import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControlLabel, LinearProgress, Link, MenuItem, Select, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import cveFeedService from '../../services/cveFeedService';
import AssetImportDialog from '../blend/AssetImportDialog';
import { parseServerTs } from '../../timezone';

const PAGE_SIZE = 50;
const RUNS_PAGE_SIZE = 25;

// Poll cadence while a run is in flight. Fast enough that the numbers visibly
// move, slow enough that four operators watching cost one row read every 1.5 s.
const POLL_MS = 4000;

const STATUS_META = {
  ok: { color: 'success', label: 'Synced' },
  running: { color: 'info', label: 'Running' },
  error: { color: 'error', label: 'Failed' },
  never_run: { color: 'default', label: 'Never run' },
  disabled: { color: 'warning', label: 'Not permitted' },
};

const RUN_STATUS_META = {
  ok: { color: 'success', label: 'Applied' },
  running: { color: 'info', label: 'Running' },
  error: { color: 'error', label: 'Failed' },
  skipped: { color: 'default', label: 'Nothing to do' },
};

const SEVERITY_COLOR = {
  critical: 'error', high: 'warning', medium: 'info', low: 'default', unknown: 'default',
};

// Why the parser threw a version statement away, in the operator's words. The
// keys are the server's closed vocabulary (CveRecordParser.SKIP_REASONS).
const SKIP_LABEL = {
  not_published: 'Record not published (rejected CVE id)',
  no_cve_id: 'No CVE id on the record',
  product_placeholder: 'Product named "n/a" — no identity to match on',
  product_key_empty: 'Product name normalized to nothing',
  status_not_affected: 'Version marked unaffected',
  version_type_unusable: 'Version ordering is rpm / deb / git, not dotted numeric',
  inclusive_upper: 'Bound is "less than or equal" — no exclusive fixed version to derive',
  no_upper_bound: 'No upper bound at all (a single version, or "*")',
  unparseable_fixed: 'Fixed version is not dotted numeric',
  row_cap: 'Record exceeded the per-CVE row cap',
};

const fmt = (n) => Number(n || 0).toLocaleString();

const when = (iso) => {
  if (!iso) return '—';
  const ms = parseServerTs(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleString() : String(iso);
};

/** Bytes as an operator reads them. 0 is "none", not "0 B" of something. */
const bytes = (n) => {
  const v = Number(n || 0);
  if (v <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i += 1; }
  return `${x >= 100 || i === 0 ? Math.round(x) : x.toFixed(1)} ${units[i]}`;
};

/** A duration in the shape a human compares against the last run. */
const secs = (ms) => {
  if (ms == null) return '—';
  const s = Math.round(Number(ms) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
};

/** How long the run in flight has been going, from the shared start time. */
const elapsedSince = (iso, now) => {
  if (!iso) return '—';
  const started = parseServerTs(iso);
  if (!Number.isFinite(started)) return '—';
  return secs(Math.max(0, now - started));
};

/**
 * Platform CVE feed — the OPERATOR's control for the advisory corpus every
 * tenant shares.
 *
 * <p>"Operator" is the person who runs the whole SaaS platform; an "admin" runs
 * a single tenant. Syncing the corpus, scheduling it and reading its history are
 * cross-tenant concerns and live only here. A tenant admin sees the findings the
 * corpus produces on their own Vulnerabilities tab and nothing on this page.
 *
 * <h3>One run, many watchers</h3>
 * Nothing about a sync is held in this component. Progress, the schedule flag
 * and the run history all come from the shared state row via the operator API,
 * so two operators on two replicas see the SAME run at the same numbers, and
 * pressing Sync while one is going joins it rather than starting a second.
 */
const OperatorCveFeedPage = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [mode, setMode] = useState('auto');
  const [importing, setImporting] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [severity, setSeverity] = useState('');

  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(1);

  const pollRef = useRef(null);
  const wasRunning = useRef(false);

  const loadState = useCallback(async () => {
    try {
      setState(await cveFeedService.status());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load the CVE feed status.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      const data = await cveFeedService.advisories({
        page, pageSize: PAGE_SIZE, q: q || undefined, severity: severity || undefined,
      });
      setRows(data.advisories || []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      setRows([]);
      setTotal(0);
    }
  }, [page, q, severity]);

  const loadRuns = useCallback(async () => {
    try {
      const data = await cveFeedService.runs({ page: runsPage, pageSize: RUNS_PAGE_SIZE });
      setRuns(data.runs || []);
      setRunsTotal(Number(data.total || 0));
    } catch (e) {
      setRuns([]);
      setRunsTotal(0);
    }
  }, [runsPage]);

  useEffect(() => { loadState(); }, [loadState]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const running = !!state?.running;

  // While a sync is in flight the shared state row is the only progress signal
  // there is — poll it, and STOP the moment the run ends. The final poll also
  // refreshes the corpus counters and the audit trail, which is the point at
  // which they actually changed.
  useEffect(() => {
    if (!running) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (wasRunning.current) {
        wasRunning.current = false;
        loadRows();
        loadRuns();
      }
      return undefined;
    }
    wasRunning.current = true;
    pollRef.current = setInterval(() => { loadState(); setNow(Date.now()); }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [running, loadState, loadRows, loadRuns]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await loadState();
      await loadRuns();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ask for a sync. A 409 is NOT a failure here: it means someone else's run is
   * already in flight and this page has joined it as an observer, so it is
   * reported as information and the poll takes over.
   */
  const startSync = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await cveFeedService.sync(mode);
      setNotice('Sync accepted — it runs in the background on the platform, so you can '
              + 'close this page and come back to it.');
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 409 && body?.running) {
        setNotice(body.error || 'A sync is already running — this page is now watching it.');
      } else {
        setError(body?.error || e?.message || 'The sync could not be started.');
      }
    } finally {
      setBusy(false);
      await loadState();
      await loadRuns();
    }
  };

  const toggleSchedule = (on) => run(async () => {
    await cveFeedService.setSchedule(on);
    setNotice(on
      ? 'The download loop is on. The platform will apply new CVE deltas every hour.'
      : 'The download loop is off. Nothing downloads on a timer — you can still sync manually.');
  });

  const clearCorpus = (source) => {
    const label = source === 'cve' ? 'the synced CVE corpus' : 'the manually imported advisories';
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Delete ${label}? This affects EVERY tenant. Their findings disappear on their next `
      + 'scan (findings are derived state, so nothing is orphaned), but until the corpus is '
      + 'synced again no tenant has anything to match against.')) return;
    run(() => cveFeedService.clear(source));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: '48px' }}><CircularProgress /></Box>;
  }

  const meta = STATUS_META[state?.status] || STATUS_META.never_run;
  const skips = state?.skips || {};
  const skipRows = Object.keys(SKIP_LABEL).filter((k) => Number(skips[k] || 0) > 0);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const runPages = Math.max(1, Math.ceil(runsTotal / RUNS_PAGE_SIZE));
  const master = !!state?.enabled;
  const startedBy = state?.triggerSource === 'schedule'
    ? 'the hourly schedule'
    : (state?.actor || 'an operator');

  return (
    <Box sx={{ p: '24px', maxWidth: '1200px', mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Vulnerability feed</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: '24px' }}>
        One advisory corpus, synced by the platform and shared by every tenant. Tenants no
        longer upload their own advisory sheets — they match their installed software against
        this copy and keep their own findings, coverage, review decisions and thresholds.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: '16px' }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: '16px' }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {!master && (
        <Alert severity="warning" sx={{ mb: '16px' }}>
          <b>This deployment is not permitted to fetch the CVE feed.</b> The master switch
          {' '}<code>inventory.vuln.cve.enabled</code> is false on the backend, so neither the
          scheduled loop nor a manual sync can run — outbound access is opt-in so the platform
          stays runnable in an air-gapped install. Until it is set true, the only way to load
          advisories is the CSV import below.
        </Alert>
      )}

      {/* ── Status and controls ───────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: '24px' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="12px">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Chip size="small" color={meta.color} label={meta.label} />
              <Typography variant="body2" color="text.secondary">
                {state?.detail || (state?.phase ? `Phase: ${state.phase}` : 'No detail recorded.')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Select
                size="small"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                sx={{ minWidth: '150px' }}
                disabled={busy || running}
              >
                <MenuItem value="auto">Auto (baseline + deltas)</MenuItem>
                <MenuItem value="baseline">Force full baseline</MenuItem>
                <MenuItem value="delta">Deltas only</MenuItem>
              </Select>
              {/* Deliberately NOT disabled while the loop is off: a manual sync is
                  exactly what an operator with the loop off needs. It IS disabled
                  while the master switch is off, because nothing can fetch. */}
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudSyncIcon />}
                disabled={busy || running || !master}
                onClick={startSync}
                sx={{ textTransform: 'none' }}
              >
                {running ? 'Sync running…' : 'Sync now'}
              </Button>
              <Tooltip title="Reload the status">
                <span>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={loadState}
                    disabled={busy} sx={{ textTransform: 'none' }}>Refresh</Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {/* ── The schedule switch ───────────────────────────────────────── */}
          <Box sx={{ mt: '16px' }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={!!state?.scheduleEnabled}
                  disabled={busy || (!master && !state?.scheduleEnabled)}
                  onChange={(e) => toggleSchedule(e.target.checked)}
                />
              )}
              label={(
                <Typography variant="body2" fontWeight={600}>
                  Download new advisories automatically, every hour
                </Typography>
              )}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {state?.scheduleEnabled
                ? 'The platform applies the CVE Program\'s hourly delta by itself, and takes a '
                  + 'fresh daily baseline when one is published. Turning this off does not stop '
                  + 'you syncing by hand.'
                : 'Nothing downloads on a timer. The corpus only changes when an operator '
                  + 'presses Sync — which stays available with this off.'}
              {state?.scheduleChangedBy
                ? ` Last changed by ${state.scheduleChangedBy} on ${when(state.scheduleChangedAt)}.`
                : ''}
            </Typography>
            {state?.scheduleBlockedReason && (
              <Alert severity="warning" sx={{ mt: '8px' }}>{state.scheduleBlockedReason}</Alert>
            )}
          </Box>

          {/* ── Live, shared progress ─────────────────────────────────────── */}
          {running && (
            <Box sx={{ mt: '16px', p: '12px', borderRadius: '6px', bgcolor: 'action.hover' }}>
              <LinearProgress />
              <Typography variant="body2" fontWeight={600} sx={{ mt: '8px' }}>
                {state?.phase || 'Starting…'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {`Started by ${startedBy} at ${when(state?.startedAt)}`}
                {state?.runOwner ? ` on ${state.runOwner}` : ''}
                {` · running ${elapsedSince(state?.startedAt, now)}.`}
                {' Everyone watching this page sees this same run — it is running on the '}
                {'platform, not in your browser, so closing the window will not stop it.'}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap="24px" sx={{ mt: '12px' }}>
                {[
                  ['Records read', fmt(state?.runRecordsSeen)],
                  ['Rows written', fmt(state?.runRowsWritten)],
                  ['Rows retired', fmt(state?.runRowsRemoved)],
                  ['Downloaded', bytes(state?.runBytes)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography variant="subtitle1" fontWeight={600}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                  </Box>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: '8px' }}>
                A full baseline reads about 570 MB. If the replica running it dies, the lock is
                taken over automatically after {state?.staleLockAfter || '3 hours'} — the corpus
                is never left half-built.
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: '16px' }} />

          <Stack direction="row" flexWrap="wrap" gap="32px">
            {[
              ['Advisories in corpus', fmt(state?.advisories)],
              ['Distinct CVEs', fmt(state?.cves)],
              ['Distinct product keys', fmt(state?.productKeys)],
              ['Baseline in force', state?.baselineDate || '—'],
              ['Last delta applied', state?.lastDeltaAsset || state?.deltaCursor || '—'],
              ['Last finished', when(state?.finishedAt)],
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                <Typography variant="h6" fontWeight={600}>{value}</Typography>
              </Box>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: '16px' }} flexWrap="wrap" gap="8px">
            <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setImporting(true)}
              disabled={busy} sx={{ textTransform: 'none' }}>
              Import advisories (CSV)
            </Button>
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
              onClick={() => clearCorpus('cve')} disabled={busy || running}
              sx={{ textTransform: 'none' }}>
              Clear synced corpus
            </Button>
            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
              onClick={() => clearCorpus('manual')} disabled={busy || running}
              sx={{ textTransform: 'none' }}>
              Clear CSV imports
            </Button>
            {running && (
              <Button size="small" color="warning" startIcon={<LockOpenIcon />}
                onClick={() => run(() => cveFeedService.unlock())} disabled={busy}
                sx={{ textTransform: 'none' }}>
                Release the sync lock
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* ── Run history — the audit trail ─────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: '24px' }}>
        <CardContent>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap="8px">
            <Typography variant="h6" fontWeight={600}>Sync history</Typography>
            <Typography variant="caption" color="text.secondary">
              {`${fmt(runsTotal)} run${runsTotal === 1 ? '' : 's'} recorded — the newest 500 are kept.`}
            </Typography>
          </Stack>
          <Table size="small" sx={{ mt: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: '8px' }}>Started</TableCell>
                <TableCell sx={{ px: '8px' }}>Result</TableCell>
                <TableCell sx={{ px: '8px' }}>Mode</TableCell>
                <TableCell sx={{ px: '8px' }}>Triggered by</TableCell>
                <TableCell sx={{ px: '8px' }} align="right">Took</TableCell>
                <TableCell sx={{ px: '8px' }} align="right">Downloaded</TableCell>
                <TableCell sx={{ px: '8px' }} align="right">Records</TableCell>
                <TableCell sx={{ px: '8px' }} align="right">Written</TableCell>
                <TableCell sx={{ px: '8px' }} align="right">Retired</TableCell>
                <TableCell sx={{ px: '8px' }}>Release</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} sx={{ px: '8px' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: '16px' }}>
                      No sync has run yet. Nothing has been downloaded and the corpus is
                      whatever it was — an empty history is not a failed history.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {runs.map((r) => {
                const rm = RUN_STATUS_META[r.status] || RUN_STATUS_META.error;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ px: '8px', whiteSpace: 'nowrap' }}>{when(r.startedAt)}</TableCell>
                    <TableCell sx={{ px: '8px' }}>
                      <Tooltip title={r.detail || ''}>
                        <Chip size="small" color={rm.color} label={rm.label} />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: '8px' }}>
                      {r.mode}
                      {r.assetsApplied > 0 ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {`${fmt(r.assetsApplied)} archive${r.assetsApplied === 1 ? '' : 's'}`}
                          {r.baselineDate ? ` · baseline ${r.baselineDate}` : ''}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ px: '8px' }}>
                      {r.trigger === 'schedule' ? 'Schedule' : (r.actor || 'Operator')}
                    </TableCell>
                    <TableCell sx={{ px: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {secs(r.durationMs)}
                    </TableCell>
                    {/* Measured at the socket. The declared archive size sits in the
                        tooltip so a truncated transfer is visible as a mismatch
                        rather than hidden behind one plausible number. */}
                    <TableCell sx={{ px: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <Tooltip title={r.archiveBytes > 0
                        ? `Release payload declared ${bytes(r.archiveBytes)} of archives`
                        : 'No archive size was published for this run'}>
                        <span>{bytes(r.bytesDownloaded)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ px: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(r.recordsSeen)}
                    </TableCell>
                    <TableCell sx={{ px: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(r.rowsWritten)}
                    </TableCell>
                    <TableCell sx={{ px: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(r.rowsRemoved)}
                    </TableCell>
                    <TableCell sx={{ px: '8px' }}>
                      <Typography variant="caption" color="text.secondary">
                        {r.releaseTag || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {/* The failure reason is a sentence, not a cell — it is shown in full
              under the row it belongs to rather than truncated into a column. */}
          {runs.filter((r) => r.status === 'error' && r.detail).slice(0, 3).map((r) => (
            <Alert key={`why-${r.id}`} severity="error" sx={{ mt: '8px' }} icon={false}>
              <Typography variant="caption">
                <b>{when(r.startedAt)}</b>
                {' — '}
                {r.detail}
              </Typography>
            </Alert>
          ))}
          {runPages > 1 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: '12px' }}>
              <Button size="small" disabled={runsPage <= 1} onClick={() => setRunsPage((p) => p - 1)}
                sx={{ textTransform: 'none' }}>Previous</Button>
              <Typography variant="body2" color="text.secondary">
                Page {runsPage} of {fmt(runPages)}
              </Typography>
              <Button size="small" disabled={runsPage >= runPages} onClick={() => setRunsPage((p) => p + 1)}
                sx={{ textTransform: 'none' }}>Next</Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── What the feed could not express ──────────────────────────────── */}
      {skipRows.length > 0 && (
        <Card variant="outlined" sx={{ mb: '24px' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600}>What the last sync could not use</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: '12px' }}>
              Most CVE records carry no version range that can be evaluated without guessing.
              Those statements are counted here rather than widened into a claim — a range that
              is invented is a CVE asserted against a version the CVE Program never named.
            </Typography>
            <Table size="small">
              <TableBody>
                {skipRows.map((k) => (
                  <TableRow key={k}>
                    <TableCell sx={{ px: '8px' }}>{SKIP_LABEL[k]}</TableCell>
                    <TableCell align="right" sx={{ px: '8px', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(skips[k])}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Corpus browser ───────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: '24px' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="12px">
            <Typography variant="h6" fontWeight={600}>Corpus ({fmt(total)} matching)</Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small" placeholder="CVE id or product" value={q}
                onChange={(e) => { setPage(1); setQ(e.target.value); }}
                sx={{ minWidth: '240px' }}
              />
              <Select size="small" value={severity} displayEmpty sx={{ minWidth: '140px' }}
                onChange={(e) => { setPage(1); setSeverity(e.target.value); }}>
                <MenuItem value="">All severities</MenuItem>
                {['critical', 'high', 'medium', 'low', 'unknown'].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>

          <Table size="small" sx={{ mt: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: '8px' }}>CVE</TableCell>
                <TableCell sx={{ px: '8px' }}>Severity</TableCell>
                <TableCell sx={{ px: '8px' }}>Product</TableCell>
                <TableCell sx={{ px: '8px' }}>Match key</TableCell>
                <TableCell sx={{ px: '8px' }}>Affected range</TableCell>
                <TableCell sx={{ px: '8px' }}>Modified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ px: '8px' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: '16px' }}>
                      {total === 0 && !q && !severity
                        ? 'The corpus is empty. Run a sync, or import a CSV.'
                        : 'Nothing matches that filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ px: '8px' }}>
                    {r.referenceUrl
                      ? <Link href={r.referenceUrl} target="_blank" rel="noreferrer">{r.cveId}</Link>
                      : r.cveId}
                  </TableCell>
                  <TableCell sx={{ px: '8px' }}>
                    <Chip size="small" color={SEVERITY_COLOR[r.severity] || 'default'}
                      label={r.cvssScore != null ? `${r.severity} ${r.cvssScore}` : r.severity} />
                  </TableCell>
                  <TableCell sx={{ px: '8px' }}>
                    {r.product}
                    {r.vendor ? <Typography variant="caption" color="text.secondary" display="block">{r.vendor}</Typography> : null}
                  </TableCell>
                  {/* The normalized key is what matching actually joins on, so it
                      is shown rather than hidden — it is the first thing to look
                      at when a tenant says "why did this not match?". */}
                  <TableCell sx={{ px: '8px' }}><code>{r.productKey}</code></TableCell>
                  <TableCell sx={{ px: '8px' }}>
                    {r.introducedVersion ? `${r.introducedVersion} ≤ v` : 'v'} &lt; {r.fixedVersion}
                  </TableCell>
                  <TableCell sx={{ px: '8px' }}>{when(r.modifiedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {pages > 1 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: '12px' }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                sx={{ textTransform: 'none' }}>Previous</Button>
              <Typography variant="body2" color="text.secondary">Page {page} of {fmt(pages)}</Typography>
              <Button size="small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                sx={{ textTransform: 'none' }}>Next</Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Attribution is a condition of the CVE Program Terms of Use. It comes
          from the server so it cannot be lost by a UI edit. */}
      <Typography variant="caption" color="text.secondary" display="block">
        {state?.attribution}
        {state?.sourceUrl ? (
          <> Source: <Link href={state.sourceUrl} target="_blank" rel="noreferrer">{state.sourceUrl}</Link>.</>
        ) : null}
      </Typography>

      {importing && (
        <AssetImportDialog
          kind="advisories"
          titleOverride="Import advisories into the shared corpus"
          onClose={(dirty) => { setImporting(false); if (dirty) { loadState(); loadRows(); } }}
        />
      )}
    </Box>
  );
};

export default OperatorCveFeedPage;
